export type SubscriptionCartPresetKey = "essential" | "growth" | "multiunit";
export type SubscriptionCartAdditionalFeature =
  | "additional_establishment"
  | "additional_provider"
  | "additional_reports";

export type SubscriptionCartState = {
  startingPoint: {
    key: SubscriptionCartPresetKey;
    title: string;
    includeAdvancedReports: boolean;
    suppressStartingPointPromotion?: boolean;
  } | null;
  additionalEstablishments: number;
  additionalProviders: number;
  includeAdvancedReports: boolean;
  updatedAt: string;
};

export const SUBSCRIPTION_CART_STORAGE_KEY = "agiliza.subscription.cart.v1";
export const SUBSCRIPTION_CART_CHANGED_EVENT = "agiliza:subscription-cart-changed";
export const SUBSCRIPTION_CART_OPEN_EVENT = "agiliza:subscription-cart-open";

export function createEmptySubscriptionCart(): SubscriptionCartState {
  return {
    startingPoint: null,
    additionalEstablishments: 0,
    additionalProviders: 0,
    includeAdvancedReports: false,
    updatedAt: new Date().toISOString(),
  };
}

function normalizeQuantity(value: unknown, max: number): number {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.min(max, Math.floor(parsed));
}

function isCurrentConfigurationTitle(value: string): boolean {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .includes("configuracao atual");
}

function normalizeCart(value: Partial<SubscriptionCartState>): SubscriptionCartState {
  const key = value.startingPoint?.key;
  const title = String(value.startingPoint?.title || "Ponto de partida");
  const startingPoint =
    key === "essential" || key === "growth" || key === "multiunit"
      ? {
          key,
          title,
          includeAdvancedReports: Boolean(value.startingPoint?.includeAdvancedReports),
          suppressStartingPointPromotion:
            Boolean(value.startingPoint?.suppressStartingPointPromotion) ||
            isCurrentConfigurationTitle(title),
        }
      : null;

  return {
    startingPoint,
    additionalEstablishments: normalizeQuantity(value.additionalEstablishments, 100),
    additionalProviders: normalizeQuantity(value.additionalProviders, 500),
    includeAdvancedReports: startingPoint?.includeAdvancedReports
      ? false
      : Boolean(value.includeAdvancedReports),
    updatedAt: value.updatedAt || new Date().toISOString(),
  };
}

export function getSubscriptionCart(): SubscriptionCartState {
  if (typeof window === "undefined") return createEmptySubscriptionCart();
  try {
    const raw = window.localStorage.getItem(SUBSCRIPTION_CART_STORAGE_KEY);
    if (!raw) return createEmptySubscriptionCart();
    return normalizeCart(JSON.parse(raw) as Partial<SubscriptionCartState>);
  } catch {
    return createEmptySubscriptionCart();
  }
}

export function getSubscriptionCartItemCount(cart: SubscriptionCartState): number {
  return (
    (cart.startingPoint ? 1 : 0) +
    normalizeQuantity(cart.additionalEstablishments, 100) +
    normalizeQuantity(cart.additionalProviders, 500) +
    (cart.includeAdvancedReports ? 1 : 0)
  );
}

export function addSubscriptionCartAdditional(
  cart: SubscriptionCartState,
  feature: SubscriptionCartAdditionalFeature,
  quantity = 1
): SubscriptionCartState {
  const amount = Math.max(1, Math.floor(Number(quantity || 1)));

  if (feature === "additional_establishment") {
    return normalizeCart({
      ...cart,
      additionalEstablishments: Math.min(
        100,
        normalizeQuantity(cart.additionalEstablishments, 100) + amount
      ),
    });
  }

  if (feature === "additional_provider") {
    return normalizeCart({
      ...cart,
      additionalProviders: Math.min(
        500,
        normalizeQuantity(cart.additionalProviders, 500) + amount
      ),
    });
  }

  return normalizeCart({
    ...cart,
    includeAdvancedReports: cart.startingPoint?.includeAdvancedReports ? false : true,
  });
}

export function shouldOpenSubscriptionCartAfterAddition(
  previousCart: SubscriptionCartState,
  nextCart: SubscriptionCartState
): boolean {
  return (
    getSubscriptionCartItemCount(previousCart) === 0 &&
    getSubscriptionCartItemCount(nextCart) > 0
  );
}

export function saveSubscriptionCart(cart: SubscriptionCartState): SubscriptionCartState {
  const next = normalizeCart({ ...cart, updatedAt: new Date().toISOString() });
  if (typeof window !== "undefined") {
    window.localStorage.setItem(SUBSCRIPTION_CART_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CART_CHANGED_EVENT));
  }
  return next;
}

export function clearSubscriptionCart(): SubscriptionCartState {
  const next = createEmptySubscriptionCart();
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(SUBSCRIPTION_CART_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CART_CHANGED_EVENT));
  }
  return next;
}

export function openSubscriptionCart(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_CART_OPEN_EVENT));
}

export function buildSubscriptionCartPayload(
  cart: SubscriptionCartState,
  couponCode?: string | null,
  options?: { allowAddonsOnly?: boolean }
) {
  if (getSubscriptionCartItemCount(cart) <= 0) return null;
  if (!cart.startingPoint && !options?.allowAddonsOnly) return null;
  return {
    startingPointPresetKey: cart.startingPoint?.key,
    additionalEstablishments: normalizeQuantity(cart.additionalEstablishments, 100),
    additionalProviders: normalizeQuantity(cart.additionalProviders, 500),
    includeAdvancedReports: Boolean(cart.includeAdvancedReports),
    suppressStartingPointPromotion: Boolean(
      cart.startingPoint?.suppressStartingPointPromotion
    ),
    couponCode: couponCode?.trim() || undefined,
  };
}
